/**
 * feat-302 — View as state (design NOTES §1, §6, QUESTIONS Q2/Q3).
 *
 * - URL is the source of truth: `?viewLocation=<location id>&viewRole=<role_key>`,
 *   written with `replace` (history.replaceState), never push.
 * - Active only once the option lists are in hand: without them the view could
 *   not be named, and an unnamed filter on an authoring page reads as missing
 *   content. The params stay in the URL untouched meanwhile.
 * - Pickers filter immediately; the old content stays on screen while the
 *   request is in flight; a response that arrives after a newer request was made
 *   is discarded (latest wins).
 * - After an audience save under View as, the set is fetched again and the
 *   edited row is PINNED until either picker changes (Q3). Purely client-side.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AudienceOptions,
  AudiencePreview,
  getAudiencePreview,
} from '../../../../services/quizContentAdminService';
import { AUDIENCE_COPY as COPY, AudienceLevel } from './audienceCopy';

export const VIEW_LOCATION_PARAM = 'viewLocation';
export const VIEW_ROLE_PARAM = 'viewRole';

export type ViewAsStatus = 'idle' | 'loading' | 'ready' | 'error';

export interface ViewAsState {
  /** Either picker set AND the option lists loaded. */
  active: boolean;
  locationId: string | null;
  roleKey: string | null;
  locationName: string | null;
  roleName: string | null;
  /** "{location or any location} · {role or any role}", or null when inactive. */
  viewName: string | null;
  status: ViewAsStatus;
  /** The id sets to filter by — the latest successful answer for the current
   *  OR previous selection while a request is in flight; null ⇒ show everything
   *  (inactive, first load, or error). */
  applied: AudiencePreview | null;
  /** Ids kept on screen although the view excludes them (Q3). */
  pinnedTrackIds: ReadonlySet<string>;
  pinnedLessonIds: ReadonlySet<string>;
  setLocation: (id: string | null) => void;
  setRole: (key: string | null) => void;
  clear: () => void;
  retry: () => void;
  /** Call after a successful audience save. `trackLessonIds` = the saved Track's
   *  Lessons, kept with it so a pinned Track is not emptied under the reader. */
  afterSave: (level: AudienceLevel, id: string, trackLessonIds?: string[]) => void;
  editedOutLine: (level: AudienceLevel, id: string) => string | null;
}

const EMPTY: ReadonlySet<string> = new Set();

export function useViewAs(args: {
  tenantId: string | null;
  searchParams: URLSearchParams;
  setSearchParams: (p: URLSearchParams, opts: { replace: boolean }) => void;
  options: AudienceOptions | null;
}): ViewAsState {
  const { tenantId, searchParams, setSearchParams, options } = args;
  const rawLoc = searchParams.get(VIEW_LOCATION_PARAM) || null;
  const rawRole = searchParams.get(VIEW_ROLE_PARAM) || null;
  const active = !!options && (rawLoc !== null || rawRole !== null);
  const locationId = active ? rawLoc : null;
  const roleKey = active ? rawRole : null;

  const locationName = locationId
    ? options?.locations.find((l) => l.id === locationId)?.name ?? locationId
    : null;
  const roleName = roleKey
    ? options?.roles.find((r) => r.role_key === roleKey)?.display_name ?? roleKey
    : null;
  const viewName = active ? COPY.viewName(locationName, roleName) : null;

  const [status, setStatus] = useState<ViewAsStatus>('idle');
  const [applied, setApplied] = useState<AudiencePreview | null>(null);
  const [pin, setPin] = useState<{
    level: AudienceLevel;
    id: string;
    tracks: ReadonlySet<string>;
    lessons: ReadonlySet<string>;
  } | null>(null);
  const seqRef = useRef(0);
  const [refreshTick, setRefreshTick] = useState(0);
  const appliedRef = useRef(applied);
  appliedRef.current = applied;

  // Latest-value refs so the setters are stable (the page's `setSearchParams`
  // changes identity on every navigation — see QuizContentReview selectionRef).
  const paramsRef = useRef(searchParams);
  paramsRef.current = searchParams;
  const setParamsRef = useRef(setSearchParams);
  setParamsRef.current = setSearchParams;

  const write = useCallback((loc: string | null, role: string | null) => {
    const p = new URLSearchParams(paramsRef.current);
    if (loc) p.set(VIEW_LOCATION_PARAM, loc);
    else p.delete(VIEW_LOCATION_PARAM);
    if (role) p.set(VIEW_ROLE_PARAM, role);
    else p.delete(VIEW_ROLE_PARAM);
    setParamsRef.current(p, { replace: true });
  }, []);

  const setLocation = useCallback(
    (id: string | null) => write(id, paramsRef.current.get(VIEW_ROLE_PARAM) || null),
    [write],
  );
  const setRole = useCallback(
    (key: string | null) => write(paramsRef.current.get(VIEW_LOCATION_PARAM) || null, key),
    [write],
  );
  const clear = useCallback(() => write(null, null), [write]);

  // A picker change (or clear, or tenant change) unpins.
  useEffect(() => {
    setPin(null);
  }, [locationId, roleKey, tenantId]);

  useEffect(() => {
    if (!tenantId || !active) {
      seqRef.current += 1;
      setStatus('idle');
      setApplied(null);
      return;
    }
    const seq = ++seqRef.current;
    setStatus('loading');
    getAudiencePreview(tenantId, locationId, roleKey)
      .then((data) => {
        if (seq !== seqRef.current) return;
        setApplied(data);
        setStatus('ready');
      })
      .catch((e) => {
        if (seq !== seqRef.current) return;
        // eslint-disable-next-line no-console
        console.error('[QuizContentReview] GET audience-preview failed:', String(e));
        setApplied(null);
        setStatus('error');
      });
  }, [tenantId, active, locationId, roleKey, refreshTick]);

  const retry = useCallback(() => setRefreshTick((n) => n + 1), []);

  const activeRef = useRef(active);
  activeRef.current = active;
  const afterSave = useCallback(
    (level: AudienceLevel, id: string, trackLessonIds: string[] = []) => {
      if (!activeRef.current) return;
      const prev = appliedRef.current;
      const visibleBefore = new Set(prev?.lesson_ids ?? []);
      setPin({
        level,
        id,
        tracks: level === 'track' ? new Set([id]) : EMPTY,
        lessons:
          level === 'lesson'
            ? new Set([id])
            : new Set(trackLessonIds.filter((l) => !prev || visibleBefore.has(l))),
      });
      setRefreshTick((n) => n + 1);
    },
    [],
  );

  const editedOutLine = useCallback(
    (level: AudienceLevel, id: string): string | null => {
      if (!pin || pin.level !== level || pin.id !== id || !applied || !viewName) return null;
      const ids = level === 'track' ? applied.track_ids : applied.lesson_ids;
      return ids.includes(id) ? null : COPY.editedOut(viewName);
    },
    [pin, applied, viewName],
  );

  return useMemo(
    () => ({
      active,
      locationId,
      roleKey,
      locationName,
      roleName,
      viewName,
      status: active ? status : 'idle',
      applied: active ? applied : null,
      pinnedTrackIds: pin?.tracks ?? EMPTY,
      pinnedLessonIds: pin?.lessons ?? EMPTY,
      setLocation,
      setRole,
      clear,
      retry,
      afterSave,
      editedOutLine,
    }),
    [
      active,
      locationId,
      roleKey,
      locationName,
      roleName,
      viewName,
      status,
      applied,
      pin,
      setLocation,
      setRole,
      clear,
      retry,
      afterSave,
      editedOutLine,
    ],
  );
}
